import { useEffect, useRef, useState, useMemo } from "react";
import { useUISlice } from "@/models/useUISlice";
import { useAppStore } from "@/controller";
import ModalContainer from "@/components/ModalContainer";
import { CheckIcon, PlusIcon, SearchIcon } from "@/components/Icons";
import { useTranslation } from "react-i18next";
import { actions } from "@/controller";
import type { Tag } from "@/types";

const EMPTY_TAGS: Tag[] = [];

const AlertTagPicker = () => {
  const { t } = useTranslation();
  const alertTagPicker = useUISlice((state) => state.alertTagPicker);
  const setAlertTagPicker = useUISlice((state) => state.setAlertTagPicker);
  const transcripts = useAppStore((state) => state.transcripts);

  const [search, setSearch] = useState("");
  const [selectedTagNames, setSelectedTagNames] = useState<Set<string>>(
    new Set()
  );
  const [loading, setLoading] = useState(false);
  // Seed the selection from the transcript's existing tags exactly once per
  // open. Keying the seed on `selectedTagNames.size === 0` (the old approach)
  // re-seeded the moment the user unchecked their last tag, making it
  // impossible to remove a transcript's final tag.
  const didSeedRef = useRef(false);

  const allTags = useAppStore(
    (state) => state.userData?.allTags || EMPTY_TAGS
  );

  // Get tag names that are already on ALL selected transcripts (for pre-selection)
  const commonTagNames = useMemo(() => {
    if (!alertTagPicker?.transcriptIds?.length) return new Set<string>();

    const selectedTranscripts = transcripts.filter((n) =>
      alertTagPicker.transcriptIds.includes(n.id)
    );
    if (selectedTranscripts.length === 0) return new Set<string>();

    // Find tag names that exist on ALL selected transcripts
    const firstTagNames: string[] = selectedTranscripts[0]?.tagsIds || [];
    const common = firstTagNames.filter((tagName) =>
      selectedTranscripts.every((t) => t.tagsIds?.includes(tagName))
    );
    return new Set<string>(common);
  }, [alertTagPicker?.transcriptIds, transcripts]);

  // Initialize selectedTagNames with common tags when modal opens — once,
  // as soon as the transcript's tags are available. After this first seed we
  // never re-apply, so the user can freely uncheck every tag (including the
  // last one) without it snapping back.
  useEffect(() => {
    if (alertTagPicker && !didSeedRef.current && commonTagNames.size > 0) {
      didSeedRef.current = true;
      setSelectedTagNames(new Set(commonTagNames));
    }
  }, [alertTagPicker, commonTagNames]);

  const filteredTags = useMemo(() => {
    if (!search.trim()) return allTags;
    const searchLower = search.toLowerCase().trim();
    return allTags.filter((t) => t.name.toLowerCase().includes(searchLower));
  }, [allTags, search]);

  const showCreateOption = useMemo(() => {
    if (!search.trim()) return false;
    const searchLower = search.toLowerCase().trim();
    return !allTags.some((t) => t.name.toLowerCase() === searchLower);
  }, [allTags, search]);

  const toggleTag = (tagName: string) => {
    setSelectedTagNames((prev) => {
      const next = new Set(prev);
      if (next.has(tagName)) {
        next.delete(tagName);
      } else {
        next.add(tagName);
      }
      return next;
    });
  };

  const handleCreateTag = async () => {
    if (!search.trim()) return;
    const result = await actions.tag.create(search.trim());
    if ("error" in result) return;
    const newTag = result.value;
    setSelectedTagNames((prev) => new Set([...prev, newTag.name]));
    setSearch("");
  };

  const handleApply = async () => {
    if (!alertTagPicker?.transcriptIds?.length) return;

    setLoading(true);

    try {
      // Add selected tags to transcripts
      if (selectedTagNames.size > 0) {
        await actions.tag.addToTranscripts(
          alertTagPicker.transcriptIds,
          Array.from(selectedTagNames)
        );
      }

      // Remove tags that were unselected (were in commonTagNames but not in selectedTagNames)
      for (const tagName of commonTagNames) {
        if (!selectedTagNames.has(tagName)) {
          for (const transcriptId of alertTagPicker.transcriptIds) {
            await actions.tag.removeFromTranscript(transcriptId, tagName);
          }
        }
      }

      alertTagPicker.onComplete?.();
      handleDismiss();
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setSearch("");
    setSelectedTagNames(new Set());
    didSeedRef.current = false;
    setAlertTagPicker(undefined);
  };

  if (!alertTagPicker) return null;

  return (
    <ModalContainer
      title={t("alertTagPicker.title")}
      onDismiss={handleDismiss}
      onPrimaryButtonPress={handleApply}
      primaryButtonText={t("alertTagPicker.applyButton")}
      loading={loading}
      modalWidth={400}
    >
      <div className="space-y-4">
        {/* Search input */}
        <div className="relative">
          <SearchIcon className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-tertiary)]" />
          <input
            dir="auto"
            data-action-id="tags.search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("alertTagPicker.searchPlaceholder")}
            className="w-full h-14 ps-12 pe-6 bg-[var(--glass-bg-hover)] rounded-3xl text-[var(--text-primary)] text-lg placeholder:text-[var(--text-tertiary)] focus:outline-none focus:bg-[var(--glass-bg-active)] transition-all shadow-inner"
            // autoFocus
          />
        </div>

        {/* Tags grid */}
        <div className="max-h-[60vh] overflow-y-auto space-y-3 p-1">
          {/* Create new tag option */}
          {showCreateOption && (
            <button
              data-action-id="tags.create"
              onClick={handleCreateTag}
              className="w-full flex items-center gap-3 px-4 py-4 bg-[var(--accent)]/10 rounded-2xl text-[var(--text-primary)] hover:bg-[var(--accent)]/20 transition-all active:scale-[0.98]"
            >
              <div className="w-10 h-10 rounded-full bg-[var(--accent)]/20 flex items-center justify-center">
                <PlusIcon className="w-5 h-5 text-[var(--accent)]" />
              </div>
              <span className="text-base font-medium">
                {t("alertTagPicker.createTag")} "{search.trim()}"
              </span>
            </button>
          )}

          {/* Existing tags */}
          {filteredTags.map((tagItem) => {
            const isSelected = selectedTagNames.has(tagItem.name);
            return (
              <button
                key={tagItem.name}
                data-action-id="tags.toggle"
                onClick={() => toggleTag(tagItem.name)}
                className={`w-full flex items-center gap-4 px-5 py-4 rounded-3xl transition-all active:scale-[0.98] ${
                  isSelected
                    ? "bg-[var(--glass-bg-active)] shadow-lg backdrop-blur-sm"
                    : "glass-tile hover:bg-[var(--glass-bg-hover)]"
                }`}
              >
                <div
                  className={`w-4 h-4 rounded-full flex-shrink-0 transition-transform ${isSelected ? "scale-110" : ""}`}
                  style={{
                    backgroundColor: tagItem.color,
                    boxShadow: isSelected
                      ? `0 0 12px ${tagItem.color}80`
                      : `0 0 8px ${tagItem.color}50`,
                  }}
                />
                <span
                  className={`text-lg text-start flex-1 truncate ${isSelected ? "font-semibold text-[var(--text-primary)]" : "text-[var(--text-secondary)]"}`}
                >
                  {tagItem.name}
                </span>
                {isSelected && (
                  <div className="w-8 h-8 rounded-full bg-[var(--accent)] flex items-center justify-center shadow-lg shadow-[var(--accent)]/30 animate-in zoom-in duration-200">
                    <CheckIcon className="w-5 h-5 text-[var(--text-inverse)]" />
                  </div>
                )}
              </button>
            );
          })}

          {/* Empty state */}
          {filteredTags.length === 0 && !showCreateOption && (
            <p className="text-center text-[var(--text-tertiary)] text-sm py-4">
              {t("alertTagPicker.noTags")}
            </p>
          )}
        </div>
      </div>
    </ModalContainer>
  );
};

export default AlertTagPicker;
